import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import identityApi from '../clients/IdentityClient';
import LogoLink from './LogoLink';
import Tooltip from './Tooltip';
import { useAuth } from '../context/AuthContext';

const SetPassword: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPasswordState] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setPassword } = useAuth();
  const mode = searchParams.get('mode');
  const isCreateMode = mode === 'create';
  const greeting = isCreateMode ? <p className="text-paragraphs">
    Enter your new password and confirm it to create your account.
  </p> : <p className="text-paragraphs">
    Enter your new password and confirm it to update your password.
  </p>;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!linkId) {
      setError('Invalid password set link');
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      if(isCreateMode) {
        const response = await setPassword({ linkId, password });
        if (response) {
          setSuccess('Password set successfully. You will be redirected to the dashboard in 3 seconds.');
          setTimeout(() => navigate('/dashboard'), 3000);
        } else {
          setError('Failed to set password. Please try again or request a new link.');
        }
      }
      else {
        const response = await identityApi.updatePassword({ linkId, password });
        if (response.ok) {
          setSuccess('Password updated successfully. You can now log in.');
          setTimeout(() => navigate('/login'), 3000);
        } else {
          const errorMessage = await response.text();
          setError(`Failed to update password. ${errorMessage}`);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="card">
          <div className="text-center mb-4">
            <LogoLink className="navbar-brand" imgClassName="me-2" />
            <h3 className="text-accent m-2 heading">Create Password</h3>
            {greeting}
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="mb-3">
              <div className="position-relative">
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  value={password}
                  onChange={(e) => setPasswordState(e.target.value)}
                  placeholder="Enter new password"
                  aria-describedby="passwordRequirements"
                  required
                  style={{ paddingRight: '40px' }}
                />
                <div style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', zIndex: 5 }}>
                  <Tooltip id="passwordRequirements" title="Password requirements">
                    <ul className="info-tooltip-list mb-0">
                      <li>At least 8 characters</li>
                      <li>Optional: Use letters, numbers & symbols for extra security</li>
                    </ul>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <input
                type="password"
                className="form-control"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>

            <div className="d-grid gap-2 mb-3">
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  'Set Password'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;


